import { useCallback } from 'react'

import { IconType } from '@factorialco/f0-react'
import {
  ArrowCycle,
  ArrowUp,
  CheckCircleLine,
  Delete,
  Download,
} from '@factorialco/f0-react/icons/app'
import {
  PaymentsDataCollectionDocument,
  RetryBankingPaymentDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { PaymentBase } from 'modules/banking/components/PaymentsDataCollection/types'
import { useMarkAsPaidPaymentExecution } from 'modules/banking/hooks/useMarkAsPaidPaymentExecution'
import { usePayBankingPayment } from 'modules/banking/hooks/usePayBankingPayment'
import { useRemoveBulkPayment } from 'modules/banking/hooks/useRemoveBulkPayment'
import { useUpdateSepaFile } from 'modules/banking/hooks/useUpdateSepaFile'
import {
  lastPaymentExecution,
  shouldShowPayWithSepaTransfer,
  supportsSepaPayment,
} from 'modules/banking/lib/paymentHelpers'

type ItemAction =
  | {
      id: string
      label: string
      icon?: IconType
      onClick: () => void
      type?: 'primary' | 'secondary' | 'other'
      critical?: boolean
    }
  | {
      type: 'separator'
    }

interface UsePaymentItemActionsProps {
  canSeeSepaTransferAction: boolean
}

export const usePaymentItemActions = ({ canSeeSepaTransferAction }: UsePaymentItemActionsProps) => {
  const { employee } = useCurrentSession()
  const { submitRemoveBulkPayment } = useRemoveBulkPayment()
  const { markAsPaidPaymentExecution } = useMarkAsPaidPaymentExecution()
  const { payPayment } = usePayBankingPayment()
  const { updateSepaFile, processDownload } = useUpdateSepaFile()
  const [retryPayment] = useMutation(RetryBankingPaymentDocument, {
    extractorFn: (result) => result.bankingMutations.retryBankingPayment,
    refetchQueries: [PaymentsDataCollectionDocument],
    awaitRefetchQueries: true,
  })

  const getItemActions = useCallback(
    (payment: PaymentBase): ItemAction[] => {
      const actions: ItemAction[] = []
      const paymentExecution = lastPaymentExecution(payment)
      const transfersCount = paymentExecution?.transfersConnection?.totalCount || 0

      const supportsSepa = supportsSepaPayment(
        payment.account?.account?.providerConfiguration?.services
      )

      const seeSepaTransferAction = supportsSepa && canSeeSepaTransferAction

      const showPayActions = shouldShowPayWithSepaTransfer(
        payment.status,
        supportsSepa,
        transfersCount > 0
      )

      // Pay with SEPA Transfer action
      if (seeSepaTransferAction && showPayActions) {
        actions.push({
          id: 'pay_with_sepa_transfer',
          label: i18n.t('banking.payments.pay_with_sepa_transfer'),
          icon: ArrowUp,
          type: 'primary',
          onClick: async () => {
            const employeeId = employee?.id
            if (!employeeId) return

            const redirectUrl = window.location.href
            const result = await payPayment(Number(payment.id), employeeId, redirectUrl)

            if (result.authorizationUrl) {
              window.location.href = result.authorizationUrl
            } else if (result.success && result.consentUrl) {
              window.location.href = result.consentUrl
            }
          },
        })
      }

      // Continue consent action (when payment is pending consent)
      if (payment.status === 'pending_consent' && paymentExecution?.consent?.consentUrl) {
        const consentUrl = paymentExecution.consent.consentUrl
        actions.push({
          id: 'continue_consent',
          label: i18n.t('banking.payments.continue_consent'),
          icon: ArrowUp,
          type: 'primary',
          onClick: () => {
            window.location.href = consentUrl
          },
        })
      }

      // Download SEPA file action
      if (!seeSepaTransferAction && paymentExecution) {
        actions.push({
          id: 'download_sepa_file',
          label: i18n.t('banking.payments.download_sepa_file'),
          icon: Download,
          onClick: () => {
            if (paymentExecution.sepaFile?.url) {
              processDownload(paymentExecution.sepaFile.url, paymentExecution.sepaFile.filename)
            } else {
              updateSepaFile(paymentExecution.id)
            }
          },
        })
      }

      // Mark as paid action
      if (!seeSepaTransferAction && paymentExecution && paymentExecution.status === 'in_progress') {
        actions.push({
          id: 'mark_as_paid',
          label: i18n.t('banking.payments.mark_as_paid'),
          icon: CheckCircleLine,
          onClick: () => {
            markAsPaidPaymentExecution(paymentExecution.id)
          },
        })
      }

      // Retry failed transfers action (when payment has rejected transfers)
      const hasRejectedTransfers = payment.status === 'partially_paid'
      if (hasRejectedTransfers) {
        actions.push({
          id: 'retry_bulk_payment',
          label: i18n.t('banking.payments.retry_bulk_payment'),
          icon: ArrowCycle,
          onClick: async () => {
            const employeeId = employee?.id
            if (!employeeId) return
            const result = await retryPayment({
              variables: { id: payment.id, payerId: employeeId },
            })
            const handled = handleResult(result, () => {
              app.setFlash({
                type: 'wrong',
                message: i18n.t('errors.generic_error'),
                time: 3000,
              })
            })
            if (handled.success) {
              app.setFlash({
                type: 'correct',
                message: i18n.t('banking.bulkPayments.detail.retryModal.success'),
                time: 3000,
              })
            }
          },
        })
      }

      // Delete bulk payment action (only if no executions or no transfers)
      if (!paymentExecution || !transfersCount) {
        if (actions.length > 0) {
          actions.push({ type: 'separator' })
        }
        actions.push({
          id: 'delete_bulk_payment',
          label: i18n.t('banking.payments.delete_bulk_payment'),
          icon: Delete,
          critical: true,
          onClick: async () => {
            await submitRemoveBulkPayment({ paymentId: payment.id })
          },
        })
      }

      return actions
    },
    [
      canSeeSepaTransferAction,
      employee,
      markAsPaidPaymentExecution,
      payPayment,
      processDownload,
      retryPayment,
      submitRemoveBulkPayment,
      updateSepaFile,
    ]
  )

  return { getItemActions }
}
